import angular from "angular";
import { PaymentChannel, Dates, Calculations, CalculationListItem, Translations, ODataResult, OData, Ajax, InvoicesLogic } from "@salaxy/core";


/**
 * The `InvoiceAlertsController` class is responsible for managing invoice alerts within the application.
 */
export class InvoiceAlertsController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "AjaxNg1"];

  /** Payment channels for OData query. */
  public paymentChannels: PaymentChannel[];

  /** Indicates if used in pro. */
  public isPro: boolean;

  public groupedPaymentChannelResult: string[] = [];

  /** Start date for filtering list of calculations with status "PaymentStarted". Displayed on front page */
  private paymentStartedWarningStartDate = Dates.getToday() + "Z";

  /** End date for filtering list of calculations with status "PaymentStarted". Displayed on front page */
  private paymentStartedWarningEndDate =  Dates.add(Dates.getToday(), -6, "month") + "Z";

  private result: ODataResult<CalculationListItem>;

  constructor(
    private calculationsApi: Calculations,
    private ajax: Ajax
  ) {}

  /**
   * Implement IController
   */
  public $onInit() {
    this.getOData().then(result => {
      this.groupResultsByPaymentChannel(result);
    });
  }

  /**
   * Groups the results by payment channel and stores the unique payment channels.
   */
  public groupResultsByPaymentChannel(results: CalculationListItem[]): void {
    const paymentChannels = results.reduce((acc, item) => {
      if (!acc.includes(item.data.paymentChannel)) {
        acc.push(item.data.paymentChannel);
      }
      return acc;
    }, [] as string[]);
    this.groupedPaymentChannelResult = paymentChannels;
  }

  /**
   * Retrieves a list of calculation items based on OData filters
   * This method fetches data from the calculations API or a specific OData endpoint depending on the value of the `isPro` property. It applies filters to retrieve items with a status of 'paymentStarted' and within a specified salary date range.
   */
  public async getOData(): Promise<CalculationListItem[]> {
    if(!this.isPro) {
      this.result = await this.calculationsApi.getOData({
        $filter: `status eq 'paymentStarted' and ${this.getPaymentChannelFilter()} and salaryDate lt ${this.paymentStartedWarningStartDate} and salaryDate gt ${this.paymentStartedWarningEndDate}`
      });
      return this.result.items ?? [];
    }
    this.result = await OData.getOData("/v01/api/pro/calculations/readonly", {$filter: `status eq 'paymentStarted' and ${this.getPaymentChannelFilter()} and salaryDate lt ${this.paymentStartedWarningStartDate} and salaryDate gt ${this.paymentStartedWarningEndDate}`}, this.ajax);
    return this.result.items ?? [];
  }


  /**
   * Generates an HTML string containing payment channel information.
   * The generated HTML includes:
   * - A header with the payment channel label.
   * - A description paragraph.
   * - Instruction text specific to the payment channel.
   * - Links related to the payment channel, including calculation and payroll links if applicable.
   * - A help link to an external article.
   */
  public getpaymentChannelInfoHtml(paymentChannel: PaymentChannel): string {
    const paymentChannelItems = this.result.items.filter(item => item.data.paymentChannel === paymentChannel);
      const getInstructionText = () => {
        return Translations.get(`SALAXY.NG1.InvoiceAlertsComponent.${paymentChannel}.instructionText.html`);
      };

      const getLinks = () => {
        if (InvoicesLogic.isPalkkausManualLike(paymentChannel)) {
          const urls = this.getUrls();
          let link = Translations.get(`SALAXY.NG1.InvoiceAlertsComponent.calculationLink.html`, { calculationUrl: urls.calculationUrl });
          if (paymentChannelItems.some(item => item.data.payrollId)) {
            link += Translations.get(`SALAXY.NG1.InvoiceAlertsComponent.payrollLink.html`, { payrollUrl: urls.payrollUrl });
          }
          return link;
        } else {
          let link = Translations.get(`SALAXY.NG1.InvoiceAlertsComponent.calculationLink.html`, { calculationUrl: "#/calc/paid/" });
          if (paymentChannelItems.some(item => item.data.payrollId)) {
            link += Translations.get(`SALAXY.NG1.InvoiceAlertsComponent.payrollLink.html`, { calculationUrl: "#/payroll/" });
          }
          return link;
        }
      };
      return `
      <div class="row">
          <div class="col-12">
            <p class="lead lead-with-text">${Translations.get("SALAXY.NG1.InvoiceAlertsComponent.header", {channel: Translations.get(`SALAXY.ENUM.PaymentChannel.${paymentChannel}.label`)})}</p>
            <p>${Translations.get("SALAXY.NG1.InvoiceAlertsComponent.description")} ${getInstructionText()} ${getLinks()}</p>
          </div>
          <div class="col-12">
            <a href="https://help.palkkaus.fi/hc/fi/articles/23187345878557-Sinulla-on-keskeneräisiä-palkkalaskelmia-ilmoitus" target="_blank">${Translations.get("SALAXY.NG1.InvoiceAlertsComponent.openHelp")}</a>
          </div>
      </div>
    `;
  }

  private getUrls() {
    const href = window.location.href;
    //Local ng1
    if (href.includes("localhost") && href.includes("/ng1/")) {
      return { calculationUrl: `#/calc/paid`, payrollUrl: `#/payroll` };
    }
    //Local pro
    if (href.includes(":9001")) {
      return { calculationUrl: `#/pro/calc-paid`, payrollUrl: `#/pro/payroll-paid` };
    }
    if (href.includes("pro.palkkaus.fi")) {
      return { calculationUrl: `#/pro/calc-paid`, payrollUrl: `#/pro/payroll-paid` };
    }
    if (href.includes("palkkaus.fi/company")) {
      return { calculationUrl: `#/calc/paid`, payrollUrl: `#/payroll` };
    }
    if (href.includes("yritys.palkkaus.fi")) {
      return { calculationUrl: `#/calc/paid`, payrollUrl: `#/payroll` };
    }
    return { calculationUrl: "", payrollUrl: "" };
  }

  private getPaymentChannelFilter(): string {
    return `(${this.paymentChannels.map(channel => `data/paymentChannel eq '${channel}'`).join(" or ")})`;
  }
}
